import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import { HumanizerVisualization } from '@ambire-common/libs/humanizer/interfaces'
import HumanizerAddress from '@common/components/HumanizerAddress'
import Text from '@common/components/Text'
import TokenOrNft from '@common/components/TokenOrNft'
import useTheme from '@common/hooks/useTheme'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'
import flexbox from '@common/styles/utils/flexbox'

interface Props {
  fullVisualization: HumanizerVisualization[]
  chainId: bigint
  accent: string
}

// The design's green "Grant approval" clause (`.zkx-sc-clause`), built from the
// humanizer's permit2 visualization: `[action, address(spender), label,
// token(token, amount), deadline]`. Returns null when the message isn't a
// token approval (e.g. a cross-chain bridge intent) so the caller can fall back
// to the generic field visualization.
const Row: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <View style={[flexbox.directionRow, flexbox.alignCenter, flexbox.justifySpaceBetween, { gap: 12 }]}>
    <Text fontSize={13} appearance="secondaryText">
      {label}
    </Text>
    {children}
  </View>
)

const GrantApprovalClause: FC<Props> = ({ fullVisualization, chainId, accent }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const { tokens, spender, deadline } = useMemo(() => {
    const tokenItems = fullVisualization.filter((i) => i?.type === 'token')
    const spenderItem = fullVisualization.find((i) => i?.type === 'address' && i.address)
    const deadlineItem = fullVisualization.find((i) => i?.type === 'deadline' && i.value)
    return { tokens: tokenItems, spender: spenderItem, deadline: deadlineItem }
  }, [fullVisualization])

  // Not a recognisable token approval — let the caller render the generic view.
  if (!tokens.length) return null

  const validUntil = deadline?.value ? new Date(Number(deadline.value)).toLocaleString() : null

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: `${accent}47`,
        borderRadius: RADII.md,
        backgroundColor: theme.controlSurfaceBackground as string,
        padding: 16,
        gap: 14
      }}
    >
      <View style={[flexbox.directionRow, flexbox.alignCenter, flexbox.justifySpaceBetween]}>
        <Text fontSize={14} weight="semiBold" color={accent}>
          {t('Grant approval')}
        </Text>
        <View
          style={{
            paddingVertical: 3,
            paddingHorizontal: 9,
            borderRadius: 100,
            backgroundColor: theme.quinaryBackground as string,
            borderWidth: 1,
            borderColor: theme.secondaryBorder as string
          }}
        >
          <Text fontSize={11} weight="medium" appearance="secondaryText">
            {t('{{i}} of {{n}}', { i: 1, n: tokens.length })}
          </Text>
        </View>
      </View>

      {tokens.map((tok) => (
        <TokenOrNft
          key={tok.id}
          value={tok.value as bigint}
          address={(tok as any).address}
          chainId={chainId}
          textSize={24}
          hideLinks
        />
      ))}

      {(!!spender?.address || !!validUntil) && (
        <View
          style={{
            gap: 8,
            paddingTop: 12,
            borderTopWidth: 1,
            borderTopColor: theme.primaryBorder as string
          }}
        >
          {!!spender?.address && (
            <Row label={t('Spender')}>
              <HumanizerAddress
                fontSize={13}
                address={spender.address}
                chainId={chainId}
                verification={spender.verification}
              />
            </Row>
          )}
          {!!validUntil && (
            <Row label={t('Valid until')}>
              <Text fontSize={13} weight="medium">
                {validUntil}
              </Text>
            </Row>
          )}
        </View>
      )}
    </View>
  )
}

export default React.memo(GrantApprovalClause)
